import { isNotValued, isValued } from './object';
import { getStringAsNumber, isEmptyString, isNotEmptyString } from './string';
import { isEmptyArray } from './array';
import { padZeros } from './number';
import { DateTime, Duration } from 'luxon';

/**
 * Determines if a given {@link Date} object is valid.
 *
 * @param date the {@link Date} object to validate.
 *
 * @return true if the given {@link Date} object is valid, false otherwise.
 */
export function isValidDate(date: Date | null | undefined): boolean {
  return isValued(date) && !Number.isNaN(date?.getTime());
}

/**
 * Parses any ISO-8601 formatted string into a {@link Date} object.
 *
 * @param dateTime string in ISO-8601 format to convert to a {@link Date} object.
 *
 * @return a {@link Date} object if a date could be parsed from the string, otherwise null.
 */
export function parseDate(dateTime: string): Date | null {
  // Note that undefined dates are reported as "Invalid Date" whereas null dates are reported as being
  // the start of the epoch (getTime() === 0).  For now, we will not do anything about this discrepancy.
  // Invalid dates can also be checked for by looking getTime() === NaN.
  const newDateTime = new Date(dateTime);
  if (isValidDate(newDateTime)) {
    return newDateTime;
  }

  return null;
}

/**
 * Parses any ISO-8601 formatted string into a {@link Date} object.
 *
 * @param date string in ISO-8601 format to convert to a {@link Date} object.  If the timestamp includes a timezone,
 * it will be included in the date identification.
 * @param time string in short form. Example: "9:00 AM CDT".  The method expects the timestamp in the current
 * computer's timezone. No conversions are done if the timezone offset isn't the same as the current computer.
 *
 * @return a {@link Date} object if a date could be parsed from the string, otherwise null.
 */
export function parseDateFromDateAndTime(date: Date, time: string): Date | null {
  if (isNotValued(date) || !isValidDate(date) || isEmptyString(time)) {
    return null;
  }

  const newDateTime = new Date(date.getTime());
  newDateTime.setHours(getStringAsNumber(time.substring(0, time.indexOf(':'))));
  newDateTime.setMinutes(getStringAsNumber(time.substring(time.indexOf(':') + 1, time.indexOf(':') + 3)));

  return newDateTime;
}

/**
 * Returns the standard localized date format for day, month and year.
 *
 * @param dateTime a {@link Date} object to get the formatted date for.
 * @param spellOutMonth true if the month should be fully spelled out, false for default short format.
 * @param locale optional locale value to adjust the formatting to a preferred regional version.  If no
 * value is given, it will default to 'en-US' locale.
 * @param timeZone optional time zone string in the format of 'America/Chicago', which forces the return value to
 * be shown for a being in that time zone.
 *
 * @return a localized string based on the given {@link Date} in the standard format ('MM/DD/YYYY' in US).
 */
export function getStandardDateFormat(
  dateTime: Date | null | undefined,
  spellOutMonth: boolean = false,
  locale: string = 'en-US',
  timeZone: string = ''
): string {
  if (!isValidDate(dateTime)) {
    return '';
  }

  let dateString: string | undefined;
  const validTimeZone = isNotEmptyString(timeZone) ? timeZone : undefined;

  if (spellOutMonth) {
    dateString = dateTime?.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: validTimeZone,
    });
  } else {
    dateString = dateTime?.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: validTimeZone,
    });
  }

  return dateString ?? '';
}

/**
 * Returns the standard non-time date format for database input ('YYYY-MM-DD').
 *
 * @param date a {@link Date} object to get the formatted date for.
 *
 * @return a string formatted as 'YYYY-MM-DD' for passing as a date into the API/database.
 */
export function getDatabaseDateString(date: Date | null | undefined): string {
  return date ? `${date.getFullYear()}-${padZeros(date.getMonth() + 1)}-${padZeros(date.getDate())}` : '';
}

/**
 * Returns a {@link Date} instance for value in the format ('YYYY-MM-DD').
 *
 * @param date string in the format 'YYYY-MM-DD'.
 *
 * @return a {@link Date} instance corresponding to the given date string ('YYYY-MM-DD').
 */
export function parseDatabaseDate(date: string): Date {
  const dateParts = date?.trim()?.split('-') || [];
  if (dateParts.length === 3) {
    const parsedDate = new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10));
    if (parsedDate.toString() !== 'Invalid Date') {
      return parsedDate;
    }
  }

  throw new Error(`Invalid date: '${date}'`);
}

/**
 * Takes a database string in the format 'YYYY-MM-DD' and converts it to the localized display version.
 *
 * @param date string in the format 'YYYY-MM-DD'.
 *
 * @return a localized string representation corresponding to the given date string ('YYYY-MM-DD').
 */
export function formatDatabaseDate(date: string): string {
  const parsedDate = parseDatabaseDate(date);
  return getStandardDateFormat(parsedDate, false, 'en-US', Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * Returns the standard localized time format in hours and minutes, which may or may not include time zone information.
 *
 * @param dateTime a {@link Date} object to get the formatted time for.
 * @param hour12 optional period boolean which determines whether 12-hour time will (true) or will not (false) be
 * used, it defaults to true
 * @param timeZoneName optional, if included, time zone will be appended to time in specified format (short or long)
 * @param timeZone optional time zone string in the format of 'America/Chicago', which forces the return value to
 * be shown as being in that time zone.
 * @param locale optional locale value to adjust the formatting to a preferred regional version.  If no
 * value is given, it will default to 'en-US' locale.
 *
 * @return a localized string based on the given {@link Date} in the standard format ("6:00 AM" or "11:32 PM" in US).
 */
export function getStandardTimeFormat(
  dateTime: Date,
  hour12: boolean = true,
  timeZoneName: 'long' | 'short' | undefined = undefined,
  timeZone: string = '',
  locale: string = 'en-US'
): string {
  if (!isValidDate(dateTime)) {
    return '';
  }
  const validTimeZone = isNotEmptyString(timeZone) ? timeZone : undefined;
  const hourCycle = hour12 ? 'h12' : 'h23';
  return (
    dateTime
      .toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: 'numeric',
        timeZone: validTimeZone,
        timeZoneName,
        hourCycle,
      })
      // Standardize spaces instead of International character code for spaces
      // See https://stackoverflow.com/questions/66409161/how-to-tell-jest-that-spaces-are-in-fact-spaces
      .replaceAll(/\s/g, ' ')
  );
}

/**
 * Similar to {@link getStandardTimeFormat}, this parses a date and returns the standard localized time format
 * in hours and minutes for that time range, which may or may not include time zone information.
 *
 * @param dateTime a {@link Date} object to get the formatted time for.
 * @param durationInMinutes the number of minutes to add to the start time to get the end time.
 * @param hour12 optional period boolean which determines whether 12-hour time will (true) or will not (false) be
 * used, it defaults to true
 * @param timeZoneName optional, if included, time zone will be appended to time in specified format (short or long)
 * @param timeZone optional time zone string in the format of 'America/Chicago', which forces the return value to
 * be shown as being in that time zone.
 * @param locale optional locale value to adjust the formatting to a preferred regional version.  If no
 * value is given, it will default to 'en-US' locale.
 *
 * @return a localized string based on the given {@link Date} in the standard format ("6:00 AM" or "11:32 PM" in US).
 */
export function getStandardTimeRangeFormat(
  dateTime: Date,
  durationInMinutes: number,
  hour12: boolean = true,
  timeZoneName: 'long' | 'short' | undefined = undefined,
  timeZone: string = '',
  locale: string = 'en-US'
): string {
  if (!isValidDate(dateTime)) {
    return '';
  }
  // Start time will never have time zone information, so override.
  const startTime = getStandardTimeFormat(dateTime, hour12, undefined, timeZone, locale);
  const endTimeObject = DateTime.fromJSDate(dateTime);
  const endTimeString = endTimeObject.plus({ minutes: durationInMinutes }).toJSDate();
  return startTime + ' - ' + getStandardTimeFormat(endTimeString, hour12, timeZoneName, timeZone, locale);
}

/**
 * Returns the ISO-8601 format representation within the UTC time zone for a given {@link Date} object
 * (this is the format the APIs generally expect for date/time).
 *
 * @param dateTime a {@link Date} object to get the formatted date for.
 *
 * @return an ISO-8601 format representation within the UTC time zone for a given {@link Date} object or null
 * if the given {@link Date} object is invalid.
 */
export function getUTCFormat(dateTime: Date): string | null {
  if (isValidDate(dateTime)) {
    // 2022-09-24T03:27:17.310Z
    return dateTime.toISOString();
  }

  return null;
}

/**
 * Returns the ISO-8601 format representation within the UTC time zone for a given date in {@link string} format
 * (this is the format the APIs generally expect for date/time).
 *
 * @param dateTime a string to get the formatted date for.
 *
 * @return an ISO-8601 format representation within the UTC time zone for a given {@link Date} object or null
 * if the given {@link Date} object is invalid.
 */
export function getUTCFormatFromDateString(dateTime: string): string | null {
  const parsedDateTime = parseDate(dateTime);
  if (parsedDateTime !== null && parsedDateTime !== undefined) {
    return getUTCFormat(parsedDateTime);
  }

  return null;
}

/**
 * Performs a temporal, mathematical operation, given a date and duration.
 * @param dateTime string in an ISO-8601 date format
 * @param operator mathematical operation to perform
 * @param duration duration to apply to the given time
 * @param timeframe duration's temporal frame of reference (year(s), month(s), week(s), day(s), or minute(s))
 * @param additionalMinutes optional minutes to be appended to duration
 * @return either a stringified {@link Date} object or an empty string if no valid date could be parsed from argument
 */
export function dateMover(
  dateTime: string,
  operator: '+' | '-',
  duration: number,
  timeframe: string,
  additionalMinutes: number = 0
): string {
  const validDate = parseDate(dateTime);
  if (!validDate) return '';

  const datePartialMover = (datePartial: number): number => {
    let trueDuration = duration;
    if (/week/i.test(timeframe)) trueDuration *= 7;
    if (operator === '+') return datePartial + trueDuration;
    return datePartial - trueDuration;
  };

  if (/year/i.test(timeframe)) ((): number => validDate.setFullYear(datePartialMover(validDate.getFullYear())))();
  else if (/month/i.test(timeframe)) ((): number => validDate.setMonth(datePartialMover(validDate.getMonth())))();
  else if (/week/i.test(timeframe)) ((): number => validDate.setDate(datePartialMover(validDate.getDate())))();
  else if (/day/i.test(timeframe)) ((): number => validDate.setDate(datePartialMover(validDate.getDate())))();
  else if (/minute/i.test(timeframe)) {
    ((): number => validDate.setMinutes(datePartialMover(validDate.getMinutes())))();
  }

  if (additionalMinutes && operator === '+') {
    ((): number => validDate.setMinutes(validDate.getMinutes() + additionalMinutes))();
  } else if (additionalMinutes && operator === '-') {
    ((): number => validDate.setMinutes(validDate.getMinutes() - additionalMinutes))();
  }

  return String(validDate);
}

/**
 * Identifies whether two {@link Date} objects reference the same date (year, month, day), excluding time.
 * If either or both of the given {@link Date} objects are not valued, the method will return false.
 *
 * @param date1 first {@link Date} to compare.
 * @param date2 second {@link Date} to compare.
 *
 * @return true if the dates are valued and match year, month, day, false otherwise.
 */
export function areSameDate(date1: Date | null | undefined, date2: Date | null | undefined): boolean {
  return (
    isValued(date1) &&
    isValued(date2) &&
    date1?.getUTCFullYear() === date2?.getUTCFullYear() &&
    date1?.getUTCMonth() === date2?.getUTCMonth() &&
    date1?.getUTCDate() === date2?.getUTCDate()
  );
}

/**
 * This method sorts a list of strings containing timestamps into proper ascending order.  The default string
 * sort for timestamps doesn't work for 12-hour times.  For example, "7:00 AM CST" should be listed before
 * "1:00 PM CST", but since 7 as a string > 1 as a string, it is not.
 *
 * @param timestamps the list of timestamps to sort into ascending order.
 *
 * @return an array of the given timestamps sorted in ascending order.
 */
export function sortTimestampsAscending(timestamps: string[]): string[] {
  if (isEmptyArray(timestamps)) {
    return timestamps;
  }

  return timestamps.sort((t1: string, t2: string) => {
    let comparisonResult;

    // Check empty values.
    if (isEmptyString(t1) && isEmptyString(t2)) {
      comparisonResult = 0;
    } else if (isEmptyString(t1)) {
      comparisonResult = -1;
    } else if (isEmptyString(t2)) {
      comparisonResult = 1;
    } else if (t1.includes(' AM ') && t2.includes(' PM ')) {
      comparisonResult = -1;
    } else if (t1.includes(' PM ') && t2.includes(' AM ')) {
      comparisonResult = 1;
    } else {
      comparisonResult = getFormattedTimestamp(t1).localeCompare(getFormattedTimestamp(t2));
    }

    return comparisonResult;
  });
}

/**
 * Takes a given date and provides a date range of start to end of the day for that date.
 *
 * @param date the date to get the date range for.
 *
 * @return the two {@link Date} objects representing the start and end of the desired day.
 */
export function getStartAndEndOfDay(date: Date): { startOfDay: Date; endOfDay: Date } {
  const targetDate = date || new Date();
  return {
    startOfDay: new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 0, 0, 0, 0),
    endOfDay: new Date(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate(), 23, 59, 59, 999),
  };
}

/**
 * Formats the given timestamp string for comparison with another timestamp.
 *
 * @param timestamp the timestamp string to format.
 *
 * @return a formatted timestamp string used for comparison.
 */
function getFormattedTimestamp(timestamp: string): string {
  let formattedTimestamp = timestamp;

  if (timestamp.startsWith('12:')) {
    formattedTimestamp = timestamp.replace('12:', '00:');
  } else if (timestamp.indexOf(':') === 1) {
    formattedTimestamp = `0${timestamp}`;
  }

  return formattedTimestamp;
}
/**
 * Formats the given timestamp string with a duration in minutes number to give a time range.
 * @param durationInMinutes the number of minutes to calculate the .
 * @param dateString the timestamp string to format
 *
 * @return a formatted time range string.
 */

export function getFormattedTimeRange(durationInMinutes: number, dateString: string): string {
  const timeFormatter: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  };
  const startDate = new Date(dateString);
  const endDate = new Date(dateString);
  endDate.setMinutes(startDate.getMinutes() + durationInMinutes);
  return `${startDate.toLocaleTimeString([], timeFormatter)} - ${endDate.toLocaleTimeString([], timeFormatter)}`;
}

/**
 * Formats the given timestamp string to return a long form date string with a suffix in the day object.
 *
 * @param timestamp the timestamp string to format.
 *
 * @return a formatted timestamp string to return a long form date string with a suffix in the day object.
 */

export function getFormattedLongDate(timestamp: string): string {
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const date = new Date(timestamp);
  const formattedDate = dateFormatter.format(date);
  const day = date.getDate();
  let suffix: string;
  if (day === 1 || day === 21 || day === 31) {
    suffix = 'st';
  } else if (day === 2 || day === 22) {
    suffix = 'nd';
  } else {
    suffix = 'th';
  }
  return `${formattedDate}${suffix}`;
}

export type DurationSummary = {
  years: number;
  months: number;
  weeks: number;
  days: number;
};

/**
 * Translates a string in the ISO-8601 time interval (duration) format and returns how many years, months, weeks,
 * and days.
 *
 * @param duration the string in the ISO-8601 time interval (duration) format.
 *
 * @return the number of years, months, weeks, and days specified by the given time interval string. If the duration
 * is invalid, all values will be 0.
 */
export function parseDuration(duration: string | null | undefined): DurationSummary {
  if (duration) {
    const durationObject = Duration.fromISO(duration);
    if (durationObject.isValid) {
      return {
        years: durationObject.years,
        months: durationObject.months,
        weeks: durationObject.weeks,
        days: durationObject.days,
      };
    }
  }

  return {
    years: Number.MIN_SAFE_INTEGER,
    months: Number.MIN_SAFE_INTEGER,
    weeks: Number.MIN_SAFE_INTEGER,
    days: Number.MIN_SAFE_INTEGER,
  };
}

/**
 * Translates an ISO-8601 duration string into a string for display. For example, "P1Y2M9D" could be translated
 * as something like 1 Year, 2 Months and 9 Days. The age display will follow the Elation rules (but not
 * necessarily the formatting):
 *   - Default: Years and Months
 *   - 16 weeks - 1 year, Age is in Months
 *   - 2 weeks - 16 weeks -> Age is in Weeks
 *   - 1 day - 13 days -> Age is in Days
 *   - Today -> 0 days
 *   - If DOB is in the future, API response as "0Y", which will show "Future" or similar.
 *
 * @param duration the duration string to translate into display format.
 *
 * @return the duration in display format or an empty string if the string cannot be translated.
 */
export function parseAndFormatDuration(duration: string | null | undefined): string {
  if (!duration) {
    return '';
  } else if (duration === 'P0Y') {
    // Special case where the birthdate is in the future for some reason.
    return 'Future';
  } else if (duration === 'P0W') {
    // Special case - this is a valid duration, but we shouldn't ever see it.
    return '0 weeks';
  }

  let durationString = '';
  const durationSummary = parseDuration(duration);

  if (durationSummary.years === 1) {
    durationString = durationString.concat(durationSummary.years + ' yr');
  } else if (durationSummary.years > 1) {
    durationString = durationString.concat(durationSummary.years + ' yrs');
  }

  if (durationSummary.months === 1) {
    durationString = durationString.concat(', ' + durationSummary.months + ' mo');
  } else if (durationSummary.months > 1) {
    durationString = durationString.concat(', ' + durationSummary.months + ' mo');
  }

  // Note that weeks should not show up in a string if it also contains years or months; it is one or the other.
  if (durationSummary.weeks === 1) {
    durationString = durationString.concat(', ' + durationSummary.weeks + ' week');
  } else if (durationSummary.weeks > 1) {
    durationString = durationString.concat(', ' + durationSummary.weeks + ' weeks');
  }

  if (!durationString && durationSummary.days === 0) {
    // Special case where the birthdate is today.
    return '0 days';
  } else if (durationSummary.days === 1) {
    durationString = durationString.concat(', ' + durationSummary.days + ' day');
  } else if (durationSummary.days > 1) {
    durationString = durationString.concat(', ' + durationSummary.days + ' days');
  }

  // Special case where there the given duration is essentially 0, due to invalid string or otherwise.
  // We will fall back to populating a 0 for every value in this scenario so it can potentially be debugged.
  if (durationString === '') {
    durationString = durationString.concat('0 yrs, 0 mo, 0 weeks, 0 days');
  }

  // Clean up ending of the string (last comma logic). If the string starts with a comma, remove it.  Otherwise,
  // convert the last comma to "and".
  if (durationString.indexOf(', ') === 0) {
    durationString = durationString.replace(', ', '');
  }

  return durationString;
}
